/**
 * A type-to-filter picker, used for accounts and for parties.
 *
 * WHY NOT A <select>
 *
 * The chart is 43 accounts and the party list will run to hundreds. A native
 * select only jumps to the first letter, so finding "6750 Charitable
 * Contributions" means scrolling past everything in the 6000s. Typing "char"
 * is the whole point.
 *
 * WHY NOT A LIBRARY
 *
 * This app has no UI dependencies and two pickers to build. A combobox is
 * about a hundred lines; react-select is a dependency, a bundle, and a set of
 * styling opinions to fight in a design that sets its own tokens.
 *
 * WHAT THE GROUPING IS FOR, AND WHAT IT MUST NOT BECOME
 *
 * `groupOrder` puts the accounts a party's role makes likely at the top —
 * revenue first for a customer, expense first for a vendor. It groups; it
 * never promotes an individual option and never preselects one.
 *
 * That line is deliberate. prototype/FINDINGS.md: "The account field is never
 * pre-filled from a suggestion. A field that is right most of the time stops
 * being read." A single most-likely account sitting at the top of the list is
 * pre-filling by another route — the operator learns the first item is usually
 * correct and stops reading it. A labelled group still makes them pick.
 */

import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface ComboboxOption {
  id: string;
  label: string;
  /** Optional heading this option is listed under. */
  group?: string;
}

export interface ComboboxProps {
  label: string;
  value: string;
  options: ComboboxOption[];
  onChange: (id: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Groups listed first, in this order. Everything else follows, in order. */
  groupOrder?: string[];
  /**
   * When given, an unmatched search offers to create it. Used by the party
   * picker: coding a bank backlog means meeting most parties mid-entry.
   */
  onCreate?: (name: string) => void;
  createLabel?: (name: string) => string;
  /**
   * Offered as a create candidate when nothing has been typed.
   *
   * The party picker passes the bank's own descriptor, so opening it on an
   * uncoded transaction leads with "add HOMEDEPOT #4412 as a party" instead of
   * an empty list. The name is only a starting point -- the create form lets
   * it be edited before anything is saved, which is what makes offering a raw
   * bank string reasonable rather than a way to end up with parties called
   * SQ *COFFEE 0012.
   *
   * Ignored when it already matches an option: the whole point is that it is
   * NOT in the list yet.
   */
  suggestedCreateName?: string;
}

// A sentinel id for the "create this" row. Plain ASCII and obviously not a
// uuid, so it cannot collide with a real option id. An earlier version used
// a control character here, which worked perfectly and made git treat the
// whole file as binary -- no diff, no blame, no review.
const CREATE_ID = '__combobox_create__';

export function Combobox({
  label,
  value,
  options,
  onChange,
  placeholder,
  disabled,
  groupOrder,
  onCreate,
  createLabel,
  suggestedCreateName,
}: ComboboxProps) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [anchor, setAnchor] = useState<{ top: number; left: number; width: number } | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  /*
   * The open list is PORTALLED to document.body, positioned from the input's
   * rectangle.
   *
   * It was an absolutely-positioned child of this component, and twice it
   * ended up underneath the line table -- reported both times as a
   * semi-transparent dropdown, because being painted over looks exactly like
   * being see-through. Two fixes were attempted from first principles (an
   * opaque background, then removing a z-index that was creating a stacking
   * context) and the second was verified as deployed and still wrong.
   *
   * So this stops arguing about which ancestor traps it. A portal has no
   * ancestors in the page's tree, so no ancestor can create a stacking context
   * around it, and position: fixed means no scroll container can clip it. The
   * class of bug is removed rather than the instance.
   *
   * The cost is that the list no longer moves with the page on its own, which
   * is why it closes on scroll and resize instead of trying to follow.
   */
  useLayoutEffect(() => {
    if (!open) {
      setAnchor(null);
      return;
    }
    const measure = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (rect) setAnchor({ top: rect.bottom, left: rect.left, width: rect.width });
    };
    measure();
    /*
     * Capture phase, because a scroll event does not bubble: a scroll inside
     * any container must count, not just the window, or the list stays put
     * while the field it is anchored to slides away.
     *
     * The ONE exemption is the list itself. Without it, capture also catches
     * the list's own overflow scroll, so a wheel over a long account list
     * dismissed the menu -- reported, correctly, as "the dropdown doesn't
     * scroll". This was introduced with the portal on 2026-09-09 and the
     * comment that used to sit here ("a scroll inside any container counts")
     * described the defect approvingly.
     */
    const close = (event: Event) => {
      // instanceof Node first: a scroll on the window itself targets the
      // DOCUMENT (or window), and contains() takes a Node. Passing a non-Node
      // is not a quiet false -- it is what made the window-scroll test fail
      // when this exemption was added.
      const target = event.target;
      if (target instanceof Node && listRef.current?.contains(target) === true) return;
      setOpen(false);
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  /*
   * Keep the keyboard cursor visible.
   *
   * The list is capped at 16rem, the chart of accounts is longer than that,
   * and the cursor is a number with a class on it -- nothing about moving it
   * scrolls anything. So arrow-down walked the highlight below the fold and
   * the list appeared frozen while the selection moved invisibly; Enter then
   * chose an account the operator could not see, which is worse than the
   * inconvenience.
   *
   * `block: 'nearest'` rather than the default, which centres the row and
   * makes every keypress jump the whole list.
   */
  useEffect(() => {
    if (!open) return;
    const current = listRef.current?.querySelector('.is-cursor');
    // Optional call: jsdom does not implement scrollIntoView, and this must
    // not be the reason a test dies.
    (current as HTMLElement | null)?.scrollIntoView?.({ block: 'nearest' });
  }, [cursor, open, anchor]);

  const selected = options.find((option) => option.id === value);

  /** Filtered and grouped, flattened back to a list so the cursor is a number. */
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matching = needle === ''
      ? options
      : options.filter((option) => option.label.toLowerCase().includes(needle));

    const groups = new Map<string, ComboboxOption[]>();
    for (const option of matching) {
      const key = option.group ?? '';
      const bucket = groups.get(key);
      if (bucket) bucket.push(option);
      else groups.set(key, [option]);
    }

    const ordered: Array<{ heading?: string; option?: ComboboxOption }> = [];
    const keys = [...groups.keys()].sort((a, b) => {
      const ai = groupOrder?.indexOf(a) ?? -1;
      const bi = groupOrder?.indexOf(b) ?? -1;
      if (ai !== bi) return (ai === -1 ? Number.MAX_SAFE_INTEGER : ai) -
        (bi === -1 ? Number.MAX_SAFE_INTEGER : bi);
      return a.localeCompare(b);
    });

    for (const key of keys) {
      if (key !== '') ordered.push({ heading: key });
      for (const option of groups.get(key)!) ordered.push({ option });
    }
    return ordered;
  }, [options, query, groupOrder]);

  const selectable = visible.filter((row) => row.option).map((row) => row.option!);

  /*
   * What a create would be called: what has been typed, or -- when nothing
   * has -- the suggested name. That second case is what puts the bank's
   * descriptor at the top of an empty party list.
   */
  const createName = query.trim() !== '' ? query.trim() : (suggestedCreateName ?? '').trim();
  const alreadyExists = options.some(
    (option) => option.label.toLowerCase() === createName.toLowerCase(),
  );
  const offersCreate = onCreate !== undefined && createName !== '' && !alreadyExists;
  const choosable: ComboboxOption[] = offersCreate
    ? [
        ...selectable,
        { id: CREATE_ID, label: (createLabel ?? ((n) => `Create “${n}”`))(createName) },
      ]
    : selectable;

  useEffect(() => {
    setCursor(0);
  }, [query, open]);

  /*
   * Closing on outside click, so the list does not sit over the balance rail.
   *
   * The LIST is checked as well as the root because the list is portalled to
   * document.body and is therefore NOT a descendant of the root any more.
   *
   * Selecting an option is safe without this -- options fire on onMouseDown,
   * so choosing one closes the list by choosing it, not by counting as an
   * outside click. What this protects is a mousedown on the list that is not
   * on an option: dragging its scrollbar, or pressing on the padding between
   * rows. Without it, a long list could not be scrolled by dragging, because
   * the grab that started the drag would close the thing being scrolled.
   */
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      const inside =
        rootRef.current?.contains(target) === true || listRef.current?.contains(target) === true;
      if (!inside) setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  function choose(option: ComboboxOption) {
    if (option.id === CREATE_ID) {
      onCreate?.(createName);
    } else {
      onChange(option.id);
    }
    setQuery('');
    setOpen(false);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      setCursor((current) =>
        Math.min(Math.max(current + delta, 0), Math.max(choosable.length - 1, 0)),
      );
      return;
    }
    if (event.key === 'Enter') {
      if (!open) return;
      event.preventDefault();
      const option = choosable[cursor];
      if (option) choose(option);
      return;
    }
    if (event.key === 'Escape') {
      if (!open) return;
      event.preventDefault();
      setQuery('');
      setOpen(false);
    }
  }

  let optionIndex = -1;

  return (
    <div className="combobox" ref={rootRef}>
      <input
        type="text"
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && choosable[cursor] ? `${listId}-${cursor}` : undefined}
        className="combobox-input"
        ref={inputRef}
        disabled={disabled}
        placeholder={placeholder}
        // Shows the selection when idle and the search while typing, so the
        // field never reads as empty when something IS chosen.
        value={open ? query : selected?.label ?? ''}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && anchor && createPortal(
        <ul
          className="combobox-list"
          ref={listRef}
          id={listId}
          role="listbox"
          aria-label={label}
          style={{ top: anchor.top, left: anchor.left, width: anchor.width }}
        >
          {visible.map((row, index) =>
            row.heading !== undefined ? (
              <li key={`h-${row.heading}-${index}`} className="combobox-group" role="presentation">
                {row.heading}
              </li>
            ) : (
              (() => {
                optionIndex += 1;
                const here = optionIndex;
                return (
                  <li
                    key={row.option!.id}
                    id={`${listId}-${here}`}
                    role="option"
                    aria-selected={here === cursor}
                    className={`combobox-option${here === cursor ? ' is-cursor' : ''}`}
                    onMouseDown={(event) => {
                      // mousedown, not click: the input blurs first otherwise
                      // and the list is gone before the click lands.
                      event.preventDefault();
                      choose(row.option!);
                    }}
                  >
                    {row.option!.label}
                  </li>
                );
              })()
            ),
          )}
          {offersCreate && (
            <li
              id={`${listId}-${selectable.length}`}
              role="option"
              aria-selected={selectable.length === cursor}
              className={`combobox-option combobox-create${
                selectable.length === cursor ? ' is-cursor' : ''
              }`}
              onMouseDown={(event) => {
                event.preventDefault();
                choose(choosable[choosable.length - 1]!);
              }}
            >
              {choosable[choosable.length - 1]!.label}
            </li>
          )}
          {choosable.length === 0 && (
            <li className="combobox-empty" role="presentation">
              {/*
                An empty box is not a failed search. With nothing typed and
                nothing to list, a "nothing matches" message reports a search
                that was never made, and leaves a picker that CAN create things
                looking like a dead end -- which is how it read the first time
                it was opened against an empty party list.
              */}
              {query.trim() === ''
                ? onCreate
                  ? 'No parties yet - type a name to add one.'
                  : 'Nothing to choose from.'
                : `Nothing matches ${JSON.stringify(query.trim())}.`}
            </li>
          )}
        </ul>,
        document.body,
      )}
    </div>
  );
}
